import { dateKeyInTimeZone, dateStringToDate, zonedDateTimeToUtc } from '@/lib/date-values';
import type { DateFilter } from './useDashboardStats';

/**
 * Map the Home date filter to an ISO [from, to] window for the executive
 * reports query. Shared by the dashboard canvas (KPIs) and the revenue chart so
 * they hit the same query key and the executive summary is fetched once.
 *
 * `to` is rounded to the end of the day (never `now` at the millisecond) so the
 * resulting query key is stable across renders and callers — otherwise React
 * Query never caches/dedupes and re-fires the request on every render.
 */
function shiftDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

export interface DashboardApiRange {
  dateFrom: string;
  dateTo: string;
}

function keysToApiRange(from: string, to: string, timeZone?: string): DashboardApiRange {
  const start = zonedDateTimeToUtc(dateStringToDate(from), '00:00', timeZone);
  const dayAfterEnd = zonedDateTimeToUtc(dateStringToDate(shiftDateKey(to, 1)), '00:00', timeZone);
  if (!start || !dayAfterEnd) {
    throw new Error(`Invalid dashboard date range: ${from} to ${to}`);
  }
  return {
    dateFrom: start.toISOString(),
    dateTo: new Date(dayAfterEnd.getTime() - 1).toISOString(),
  };
}

export function dateFilterToRange(
  filter: DateFilter,
  referenceDate = new Date(),
  timeZone?: string,
): { from?: string; to?: string } {
  const today = dateKeyInTimeZone(referenceDate.toISOString(), timeZone);
  if (filter.period === 'custom' && filter.startDate && filter.endDate) {
    return { from: filter.startDate, to: filter.endDate };
  }
  switch (filter.period) {
    case 'today':
      return { from: today, to: today };
    case 'this_week': {
      const dayOfWeek = new Date(`${today}T12:00:00Z`).getUTCDay();
      return { from: shiftDateKey(today, -dayOfWeek), to: today };
    }
    case 'this_month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'all_time':
      return { from: '2000-01-01', to: today };
    default:
      return {};
  }
}

/**
 * Inclusive UTC instants for list endpoints that filter by `createdAt`.
 * Dashboard filters are tenant-local calendar days; passing bare YYYY-MM-DD
 * values to those endpoints would make JavaScript interpret them as UTC and
 * lose records near midnight in non-UTC tenants.
 */
export function dateFilterToApiRange(
  filter: DateFilter,
  referenceDate = new Date(),
  timeZone?: string,
): DashboardApiRange {
  const range = dateFilterToRange(filter, referenceDate, timeZone);
  if (!range.from || !range.to) {
    throw new Error('Dashboard date filter did not resolve to a bounded range');
  }
  return keysToApiRange(range.from, range.to, timeZone);
}

/** Previous inclusive window with the same number of tenant-local days. */
export function previousDashboardApiRange(
  filter: DateFilter,
  referenceDate = new Date(),
  timeZone?: string,
): DashboardApiRange {
  const range = dateFilterToRange(filter, referenceDate, timeZone);
  if (!range.from || !range.to) {
    throw new Error('Dashboard date filter did not resolve to a bounded range');
  }
  const spanDays =
    Math.round(
      (Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / 86_400_000,
    ) + 1;
  const previousTo = shiftDateKey(range.from, -1);
  const previousFrom = shiftDateKey(range.from, -spanDays);
  return keysToApiRange(previousFrom, previousTo, timeZone);
}
